import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import Radio from '../InputFields/Radio';
import CheckboxGTT from '../InputFields/CheckboxGTT';
import { variables } from '../../assets/styles/variables';
import { pxToRem } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import {
  ModalCloseIcon,
  WeightGrid,
  WeightOption,
  FullWidthApply,
} from './Common';

const DrawerTitle = styled.span`
  font-family: ${variables.family.goldSilver};
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: ${themeColors.colorTextPrimary};
`;

const AllWeightsSubtitle = styled.span`
  display: block;
  font-size: ${pxToRem(14)};
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  margin-top: ${pxToRem(4)};
`;

const AllWeightsTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  font-family: ${variables.family.goldSilver};
  line-height: 18px;
  letter-spacing: 0%;
`;

const AllWeightsRadio = styled(Radio)`
  &&& {
    background-color: ${themeColors.colorBackgroundSecondary};
    ${({ selected }) =>
      selected && `border-color: ${themeColors.colorTextSecondary};`}
  }
`;

// A single "All Weights" (= no weight filter) radio card and a grid of
// weight checkboxes. The radio and the checkboxes are mutually exclusive:
// selecting the radio clears every checkbox; checking any weight clears the
// radio; unchecking the last weight re-selects the radio. "Apply" reports the
// selected weights (empty array = all) to the parent screen.
const SelectWeightDrawer = ({
  isOpen,
  onClose,
  availableWeights,
  selectedWeights,
  onApply,
}) => {
  const { t } = useTranslation();
  // Draft selection: edits stay local until "Apply". Empty = "All Weights".
  const [draftWeights, setDraftWeights] = useState(selectedWeights);

  // Re-seed the draft from the applied selection every time the drawer opens.
  useEffect(() => {
    if (isOpen) {
      setDraftWeights(selectedWeights);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isAllSelected = draftWeights.length === 0;

  const selectAllWeights = () => setDraftWeights([]);

  const toggleWeight = (weight) => {
    setDraftWeights((prev) =>
      prev.includes(weight)
        ? prev.filter((w) => w !== weight)
        : [...prev, weight],
    );
  };

  const handleApply = () => {
    onApply(draftWeights);
    onClose();
  };

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <DrawerTitle>{t('gtt.triggerOrders.weight.drawerTitle')}</DrawerTitle>
      }
      closeIcon={<ModalCloseIcon data-cy="gtt-weight-close" />}
      footer={
        <FullWidthApply>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            type="button"
            onClick={handleApply}
            data-cy="gtt-weight-apply"
          >
            {t('common.apply')}
          </Button>
        </FullWidthApply>
      }
    >
      <AllWeightsRadio
        variant="RadioBox"
        name="gtt-weight-all"
        cssIndicator
        circleWidth="16px"
        circleHeight="16px"
        circleBorderWidth="2px"
        selected={isAllSelected}
        checked={isAllSelected}
        onClick={selectAllWeights}
        data-cy="gtt-weight-all"
      >
        <span>
          <AllWeightsTitle>
            {t('gtt.triggerOrders.weight.allWeights')}
          </AllWeightsTitle>
          <AllWeightsSubtitle>
            {t('gtt.triggerOrders.weight.allWeightsSubtitle')}
          </AllWeightsSubtitle>
        </span>
      </AllWeightsRadio>

      <WeightGrid>
        {availableWeights.map((weight) => {
          const checked = draftWeights.includes(weight);
          return (
            <WeightOption
              key={weight}
              onClick={() => toggleWeight(weight)}
              data-cy={`gtt-weight-${weight}`}
            >
              <CheckboxGTT
                checked={checked}
                ariaLabel={weight}
                name={`gtt-weight-${weight}`}
                thickBorder
                onClick={(e) => {
                  e.stopPropagation();
                  toggleWeight(weight);
                }}
              />
              {weight}
            </WeightOption>
          );
        })}
      </WeightGrid>
    </BottomDrawer>
  );
};

SelectWeightDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  availableWeights: PropTypes.arrayOf(PropTypes.string),
  selectedWeights: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func,
};

SelectWeightDrawer.defaultProps = {
  availableWeights: [],
  selectedWeights: [],
};

export default SelectWeightDrawer;
